import { useState } from 'react';

import ScriptView from '../script/ScriptView';
import PropSection from './PropSection';
import Modal from '../utils/Modal';

// Properties inspector shown when 2+ elements are multi-selected. It only
// exposes properties shared by *every* selected element (the intersection of
// their `props` keys), so type-specific things (e.g. dropdown contents) simply
// don't appear unless all selected elements are the same kind. Edits apply to
// the whole selection at once.
export default function MultiProperties({
    comps,
    setComps,
    multiSel,
    setMultiSel,
    setSel,
    vars,
    setVars,
    externals,
    setExternals,
    creationIndex,
    setCreationIndex
}) {

    const [isShowingScript, setIsShowingScript] = useState(false);
    const [showScriptWarning, setShowScriptWarning] = useState(false);

    const firstId = multiSel[0];

    // A script is "populated" if any of its event slots has children.
    const scriptIsPopulated = (el) =>
        Array.isArray(el?.script) &&
        el.script.some((ev) => ev && Array.isArray(ev.children) && ev.children.length > 0);
    const anyPopulated = multiSel.some((id) => scriptIsPopulated(comps[id]));

    // A prop is editable across the group only if every selected element has it.
    const commonHas = (key) =>
        multiSel.every((id) => comps[id]?.props &&
            Object.prototype.hasOwnProperty.call(comps[id].props, key));

    const displayVal = (key) => comps[firstId].props[key];
    const allTrue = (key) => multiSel.every((id) => comps[id].props[key]);
    const allAlign = (val) => multiSel.every((id) => comps[id].props.alignment === val);

    const setAll = (key, value) => {
        let new_comps = [...comps];
        multiSel.forEach((id) => {
            new_comps[id] = { ...new_comps[id], props: { ...new_comps[id].props, [key]: value } };
        });
        setComps(new_comps);
    };

    // Editing the script targets the first element; on close we copy that
    // script onto every selected element so they share one common script.
    const handleScriptShowing = (val) => {
        if (val === false) {
            const scriptStr = JSON.stringify(comps[firstId].script);
            let new_comps = [...comps];
            multiSel.forEach((id) => {
                new_comps[id] = { ...new_comps[id], script: JSON.parse(scriptStr) };
            });
            setComps(new_comps);
        }
        setIsShowingScript(val);
    };

    const hasText = commonHas('fontSize') || commonHas('bold') || commonHas('italic') ||
        commonHas('underlined') || commonHas('alignment') || commonHas('color');
    const hasBorder = commonHas('borderEnabled') || commonHas('borderColor') || commonHas('borderWidth');

    return (<>
        <p className="prop-section-title" style={{ marginBottom: 6 }}>
            {multiSel.length} elements selected
        </p>

        <div className="prop-actions">
            <a className='panel-button' onClick={(e) => {
                e.preventDefault();
                // Warn first if overwriting any pre-existing scripts.
                if (anyPopulated) {
                    setShowScriptWarning(true);
                } else {
                    setIsShowingScript(true);
                }
            }}>Edit Script</a>
        </div>
        <p className='panel-label' style={{ marginBottom: '0.5vmax' }}>
            Editing the script applies one common script to all {multiSel.length} elements.
        </p>

        <hr style={{
            border: 'none',
            borderTop: '1px solid var(--ed-border)',
            margin: '12px 0'
        }} />

        {hasText ? (
            <PropSection title="Text">
                {commonHas('fontSize') ? <>
                    <p className='panel-label'>Size: </p>
                    <input className='panel-input' onChange={(e) => {
                        setAll('fontSize', e.target.value);
                    }} value={displayVal('fontSize')} />
                    <br /> <br />
                </> : null}

                {(commonHas('bold') || commonHas('italic') || commonHas('underlined')) ? <>
                    <p className='panel-label'>Style: </p>
                    {commonHas('bold') ? <i className={'fa fa-solid fa-bold panel-icon' + (allTrue('bold') ? ' prop-toggle-active' : '')} onClick={() => {
                        setAll('bold', !allTrue('bold'));
                    }}></i> : null}
                    {commonHas('italic') ? <i className={'fa fa-solid fa-italic panel-icon' + (allTrue('italic') ? ' prop-toggle-active' : '')} onClick={() => {
                        setAll('italic', !allTrue('italic'));
                    }}></i> : null}
                    {commonHas('underlined') ? <i className={'fa fa-solid fa-underline panel-icon' + (allTrue('underlined') ? ' prop-toggle-active' : '')} onClick={() => {
                        setAll('underlined', !allTrue('underlined'));
                    }}></i> : null}
                    <br /> <br />
                </> : null}

                {commonHas('alignment') ? <>
                    <p className='panel-label'>Alignment: </p>
                    <i className={'fa fa-solid fa-align-left panel-icon' + (allAlign('left') ? ' prop-toggle-active' : '')} onClick={() => {
                        setAll('alignment', 'left');
                    }}></i>
                    <i className={'fa fa-solid fa-align-center panel-icon' + (allAlign('center') ? ' prop-toggle-active' : '')} onClick={() => {
                        setAll('alignment', 'center');
                    }}></i>
                    <i className={'fa fa-solid fa-align-right panel-icon' + (allAlign('right') ? ' prop-toggle-active' : '')} onClick={() => {
                        setAll('alignment', 'right');
                    }}></i>
                    <br /> <br />
                </> : null}

                {commonHas('color') ? <>
                    <p className='panel-label'>Text Color: </p>
                    <input className='panel-input' style={{
                        width: '17.5%',
                        marginRight: '0.75vmax'
                    }} onChange={(e) => {
                        setAll('color', e.target.value);
                    }} value={displayVal('color')} />
                    <p className='panel-label'>or</p>
                    <input type='color' className='panel-color-input' onChange={(e) => {
                        setAll('color', e.target.value);
                    }} value={displayVal('color')} />
                </> : null}
            </PropSection>
        ) : null}

        {commonHas('backgroundColor') ? (
            <PropSection title="Appearance">
                <p className='panel-label'>Background Color: </p>
                <input className='panel-input' style={{
                    width: '17.5%',
                    marginRight: '0.75vmax'
                }} onChange={(e) => {
                    setAll('backgroundColor', e.target.value);
                }} value={displayVal('backgroundColor')} />
                <p className='panel-label'>or</p>
                <input type='color' className='panel-color-input' onChange={(e) => {
                    setAll('backgroundColor', e.target.value);
                }} value={displayVal('backgroundColor')} />
            </PropSection>
        ) : null}

        {hasBorder ? (
            <PropSection title="Border">
                {commonHas('borderEnabled') ? <>
                    <p className='panel-label'>Border Enabled: </p>
                    <input type="checkbox" checked={allTrue('borderEnabled')} onClick={() => {
                        setAll('borderEnabled', !allTrue('borderEnabled'));
                    }}></input>
                    <br /><br />
                </> : null}
                {commonHas('borderColor') ? <>
                    <p className='panel-label'>Border Color: </p>
                    <input className='panel-input' style={{
                        width: '17.5%',
                        marginRight: '0.75vmax'
                    }} onChange={(e) => {
                        setAll('borderColor', e.target.value);
                    }} value={displayVal('borderColor')} />
                    <p className='panel-label'>or</p>
                    <input type='color' className='panel-color-input' onChange={(e) => {
                        setAll('borderColor', e.target.value);
                    }} value={displayVal('borderColor')} />
                    <br /><br />
                </> : null}
                {commonHas('borderWidth') ? <>
                    <p className='panel-label'>Border Width:</p>
                    <input type='range' min='1' max='10' value={displayVal('borderWidth')} onChange={(e) => {
                        setAll('borderWidth', parseInt(e.target.value));
                    }}></input>
                </> : null}
            </PropSection>
        ) : null}

        <div className="prop-footer">
            <a className='panel-button' onClick={(e) => {
                e.preventDefault();
                setMultiSel([]);
                setSel(-1);
            }}>Deselect</a>
            <a className='panel-button prop-danger' onClick={(e) => {
                e.preventDefault();
                let new_comps = [...comps];
                [...multiSel].sort((a, b) => b - a).forEach((i) => new_comps.splice(i, 1));
                setComps(new_comps);
                setMultiSel([]);
                setSel(-1);
            }}>Delete All</a>
        </div>

        {isShowingScript ? <ScriptView id={firstId} comps={comps} setComps={setComps}
            setIsShowingScript={handleScriptShowing}
            vars={vars} setVars={setVars} externals={externals} setExternals={setExternals}
            creationIndex={creationIndex} setCreationIndex={setCreationIndex} /> : <></>}

        {showScriptWarning ? <Modal light width='26' height='30'>
            <p style={{
                fontWeight: 600,
                fontSize: '1.15vmax',
                marginTop: '3vh',
                padding: '0 1.5vmax'
            }}>Overwrite existing scripts?</p>
            <p style={{
                fontWeight: 300,
                fontSize: '0.95vmax',
                padding: '0.75vmax 1.75vmax',
                color: 'var(--ed-text-soft)',
                lineHeight: 1.5
            }}>
                One or more of the {multiSel.length} selected elements already has a
                script. Editing a common script replaces the script on every selected
                element with the one you edit here — their existing scripts will be lost.
            </p>
            <div style={{ marginTop: '1.5vh' }}>
                <a className='panel-button' style={{ marginRight: '1vmax' }} onClick={(e) => {
                    e.preventDefault();
                    setShowScriptWarning(false);
                    setIsShowingScript(true);
                }}>Continue</a>
                <a className='panel-button prop-danger' onClick={(e) => {
                    e.preventDefault();
                    setShowScriptWarning(false);
                }}>Cancel</a>
            </div>
        </Modal> : <></>}
    </>);

}
